import { Outlet } from 'react-router-dom'

import { useQuery } from '@apollo/client'
import {
  ApplicantForRequestsDataTableFragment,
  CompetencyFragment,
  GetCompetenciesDocument,
  RequestsPageDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useDebouncedSearch } from 'modules/core/hooks/useSearch'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { RequestsDataTable } from 'modules/trainings/components/Requests/RequestsDataTable'
import { Provider } from 'modules/trainings/components/Requests/RequestsDataTable/Provider'
import { RequestsEmptyState } from 'modules/trainings/components/Requests/RequestsEmptyState'

export default function Requests() {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { data, loading, error } = useQuery(RequestsPageDocument, {
    variables: {
      companyId,
    },
  })
  const { data: competencies } = useQuery(GetCompetenciesDocument)
  const isEmpty = data?.trainings.trainingRequestsConnection.totalCount === 0
  return (
    <ResponsivePage.Body
      loading={loading}
      error={error}
      variant="full"
      showHeader={!isEmpty}
      authorized={data?.trainings._policies.trainingsAccessManagementSpace.can}
    >
      {data && (
        <Content
          emptyCatalog={data.trainings.trainingsConnection.totalCount === 0}
          canCreate={Boolean(data.trainings._policies.trainingsRequestsCreate.can)}
          settingsEnabled={Boolean(
            data.trainings.settingsConnection.nodes[0]?.trainingRequestsEnabled
          )}
          isEmpty={isEmpty}
          requests={data.trainings.trainingRequestsConnection.edges.map(({ node }) => node)}
          competencies={competencies?.competencies.competencies ?? []}
        />
      )}
      <Outlet
        context={{
          requests: data?.trainings.trainingRequestsConnection.edges,
          settingsEnabled: data?.trainings.settingsConnection.nodes[0]?.trainingRequestsEnabled,
        }}
      />
    </ResponsivePage.Body>
  )
}

function Content({
  isEmpty,
  requests,
  canCreate,
  settingsEnabled,
  emptyCatalog,
  competencies,
}: {
  isEmpty: boolean
  requests: ApplicantForRequestsDataTableFragment[]
  canCreate: boolean
  settingsEnabled: boolean
  emptyCatalog: boolean
  competencies: CompetencyFragment[]
}) {
  const { search, debouncedSearch, onSearch } = useDebouncedSearch()
  if (isEmpty)
    return (
      <RequestsEmptyState
        canCreate={canCreate}
        emptyCatalog={emptyCatalog}
        settingsEnabled={settingsEnabled}
      />
    )

  return (
    <Provider search={debouncedSearch} requests={requests} competenciesData={competencies}>
      <RequestsDataTable search={search} onSearch={onSearch} />
    </Provider>
  )
}
